/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import _ from 'lodash';
import { toEmojiText } from './preview-html';

// Helpers for the mixed items (article, status, topic, ...) returned by the community feeds.

export interface ItemRoute {
  state: string;
  params: Record<string, unknown>;
}

const TYPE_LABELS: Record<string, string> = {
  topic: 'Discussion',
  referral_campaign: 'Referral Campaign',
};

export function itemTypeLabel(item: any): string {
  return TYPE_LABELS[item.type] || _.capitalize(item.type);
}

// Names the item in action labels, so "Like" etc. say which card they belong to.
export function itemContext(item: any): string {
  if (item.title) return toEmojiText(item.title);
  return item.user?.name ? `post by ${item.user.name}` : 'this post';
}

// Items without a person behind them show an icon instead of an avatar.
const GLYPHS: Record<string, string> = {
  course: 'school',
  project: 'assignment',
  referral_campaign: 'group_add',
  tool: 'build',
};

export function itemGlyph(item: any): string | undefined {
  return GLYPHS[item.type];
}

export function itemTitle(item: any): string {
  return item.title || item.name || item.content;
}

// Shortened in code rather than clamped in CSS, so larger text spacing never clips it.
export function shorten(text: string, length: number): string {
  return text.length > length ? `${text.slice(0, length).replace(/\s+\S*$/, '')}…` : text;
}

export function itemRoute(item: any): ItemRoute | null {
  switch (item.type) {
    case 'article':
      return { state: 'singlearticle', params: { articleId: item.slug } };
    case 'course':
      return { state: 'singlecourse', params: { courseId: item.slug } };
    case 'project':
      return { state: 'singleproject', params: { projectId: item.slug } };
    case 'topic':
      return { state: 'singlediscussionpage', params: { page: 1, discussionId: item.id } };
    case 'status':
      return { state: 'singlestatus', params: { statusId: item.id } };
    case 'referral_campaign':
      return { state: 'referralCampaignsShow', params: { referralCampaignId: item.slug } };
    case 'tool':
      return { state: 'singletool', params: { toolId: item.slug } };
    default:
      return null;
  }
}

export function itemLikeCount(item: any): number {
  return _.get(item, 'likes.count', 0);
}

// The API's comment / reply count. (Topics used to have one subtracted for an opening post stored as the
// first comment; that's rare now and the API's number is the right one.)
export function itemCommentCount(item: any): number {
  const count = item.comments ? item.comments.count : item.post_count;
  return count > 0 ? count : 0;
}
